"use client";
import React, { useEffect, useRef, useState } from "react";
import "@/styles/carousel-base.css";
import AudioPlayer from "@/components/Audio/AudioPlayer";
import { Post, Widget } from "@pulsex/types";
import ImageUI from "../CustomImage/ImageUI";
import Author from "../Author";
import Link from "next/link";
import SocialsList from "./Socials";
// export const runtime = "edge";

export default function ReadingSide({
    widget,
    relatedPosts,
}: {
    widget: Widget;
    relatedPosts?: Post[];
}) {
    const [cleanContent, setCleanContent] = useState("");
    const { posts } = widget;
    const post = posts[0];
    const source = post?.findMeta((m) => m.key === "source");

    const contentContainerRef = useRef<HTMLDivElement>(null);
    const relatedPostsAdded = useRef(false);

    useEffect(() => {
        const purify = (html: string) => {
            // Remove <style> tags from HTML content
            let purifiedHtml = html.replace(/<style>.*<\/style>/g, "");
            // Remove <figure> and <figcaption> elements with any inner content
            purifiedHtml = purifiedHtml.replace(
                /<figure\b[^>]*>(.*?)<\/figure>/g,
                ""
            );
            purifiedHtml = purifiedHtml.replace(
                /<figcaption\b[^>]*>(.*?)<\/figcaption>/g,
                ""
            );
            setCleanContent(purifiedHtml);
        };
        if (post.content) {
            purify(post.content);
        }
    }, [post.content]);

    useEffect(() => {
        if (!relatedPosts?.length || !cleanContent || relatedPostsAdded?.current) return;
        const P_TAG_INDENTIFIER = `<p style="text-align: justify;">`
        const pTagsCount = cleanContent.split(P_TAG_INDENTIFIER).length - 1;

        if (pTagsCount > 1) {
            const middleIndex = Math.floor(pTagsCount / 2);
            const htmlToInsert = getRelatedPostsHTML(relatedPosts);
            const updatedContent = cleanContent.split(P_TAG_INDENTIFIER).map((part, index) => {
                if (index === middleIndex) {
                    return part + htmlToInsert;
                }
                return part;
            }).join(P_TAG_INDENTIFIER);
            setCleanContent(updatedContent);
            relatedPostsAdded.current = true;
        }
    }, [cleanContent, relatedPosts]);

    if (!post) return "";

    return (
        <div className="max-w-screen-xl mx-auto ">
            <div className="flex justify-between items-center flex-wrap pt-2 pb-3 text-gray-400 text-[15px]">
                <div className="flex items-center flex-wrap text-sm text-gray-400 dark:text-white mb-2 divide-x divide-gray-400">
                    {post?.author?.name && (
                        <Author
                            className="flex items-center gap-1 group"
                            author={post.author}
                        >
                            {/* if the author is Al Mayadeen English, don't show the author icon */}
                            {post.author.name !== "Al Mayadeen English" && (
                                <div>
                                    <ImageUI
                                        data={{
                                            image: post.author?.icon,
                                        }}
                                        ImageElementClassName="object-cover inset-0"
                                        cropSize="square"
                                        className="rounded-full object-cover bg-transparent w-12 h-12 lg:w-5 lg:h-5"
                                        needsMediaURL={true}
                                    />
                                </div>
                            )}
                            <div>
                                <p
                                    className={
                                        "text-sm dark:text-white dark:opacity-100 lg:text-xs group-hover:text-primary"
                                    }
                                >
                                    {post.author.name} &nbsp;
                                </p>
                            </div>
                        </Author>
                    )}

                    {source && (
                        <span>
                            &nbsp;{source.title}: {source.value} &nbsp;
                        </span>
                    )}

                    {post.displayDate && (
                        <span>&nbsp; {post.displayDate} &nbsp;</span>
                    )}
                    {post.shares !== null ? (
                        <span>&nbsp; {post.shares || 0} Shares</span>
                    ) : (
                        ""
                    )}
                </div>
                {post.readingTimeInMinutes && (
                    <span className="flex items-center justify-center gap-1 ml-auto">
                        <img
                            src="/svgs/icons/timer.svg"
                            height={20}
                            width={20}
                        />
                        {post.readingTimeInMinutes} min read
                    </span>
                )}
            </div>
            <p className="text-xl italic dark:text-white">{post.summary}</p>
            <div className="flex justify-between items-center py-4 text-gray-400  text-sm lg:flex-col lg:items-start lg:flex-col-reverse lg:pt-2">
                <AudioPlayer post={post} /> <SocialsList post={post} />
            </div>
            <div className="bg-[#F7F7F7] rounded-xl">
                <div className="relative overflow-hidden rounded-xl">
                    <ImageUI
                        data={post}
                        cropSize={"rectangle"}
                        resolution={4}
                    />
                </div>
                {post?.imageCaption && (
                    <div className="py-3 px-4 text-[15px] lg:py-2 lg:px-2">
                        {post?.imageCaption}
                    </div>
                )}
            </div>
            {cleanContent && (
                <div className="post-details-content pb-4">
                    <div
                        dangerouslySetInnerHTML={{
                            __html: cleanContent,
                        }}
                        ref={contentContainerRef}
                        className="prose text-xl font-medium text-black dark:text-white mt-6 space-y-5 lg:text-base"
                    ></div>
                    {post.disclaimer && (
                        <p className="pt-10 text-gray-500 text-lg">
                            {post.disclaimer}
                        </p>
                    )}
                </div>
            )}

            <div className="flex flex-wrap ">
                {post.keywords.map((Tab: any, index: any) => (
                    <Link
                        href={`/tags/${Tab}`}
                        key={index}
                        className="border border-primary rounded-lg px-5 py-1 transition-all mr-4 mt-4 lg:mr-2 lg:mt-2 hover:bg-primary text-black/70 dark:text-white/80 hover:text-white/80"
                    >
                        <span className="text-lg lg:text-sm">{Tab}</span>
                    </Link>
                ))}
            </div>
            {/* dont remove below div for any reason, its for tailwind to recognize these classes and compile them in the final css file */}
            <div className="hidden absolute -top-1 left-0 -translate-y-1/2 text-2xl font-bold py-7 bg-white w-fit pr-2 col-span-6 py-5 border-t border-b border-gray-700 space-x-3 items-center hover:text-primary"></div>
        </div>
    );
}

function getRelatedPostsHTML(relatedPosts: Post[]) {
    return `
        <div class='gap-4 py-7 !my-10 border-t border-b border-gray-400 relative'>
            <div class='absolute -top-1 left-0 -translate-y-1/2 text-2xl font-bold bg-white w-fit pr-2 dark:bg-[#1c2931]'>Related Stories</div>
            <div class="flex md:flex-col gap-x-7 gap-y-4">
            ${relatedPosts?.map(post => `
                <a href=${post.url} class='flex items-center sm:flex-col sm:items-start'>
                    <img class='rounded-lg object-cover min-w-40 max-w-40 h-24 sm:min-w-full sm:max-w-full sm:h-auto sm:max-h-40' src=${post.image} />
                    <p class='text-lg md:text-base sm:text-xs text-black dark:text-white hover:text-primary ml-2 xs:ml-0 md:mt-2'>${post.title}</p>
                </a>`).join('')
            }
            </div>
        </div>
    `;
}
